'use client'

/**
 * MessageErrorBoundary
 *
 * Isolates a single chat message's render so a failure in one message (e.g. a
 * transient DOM reconciliation error during the streaming->final transition, or
 * a third-party renderer like Mermaid mutating the DOM) cannot cascade and
 * destabilize the entire message list.
 *
 * On error it renders the message's raw text content as a graceful fallback and
 * auto-recovers when the message content changes (e.g. after the stream
 * finalizes or on re-render), so a one-frame reconciliation hiccup does not
 * leave the message permanently broken.
 *
 * Primary responsibilities:
 * - Catch render errors from a single message subtree
 * - Degrade to plain-text content instead of unmounting the whole list
 * - Reset automatically when the underlying content changes
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { safeConsole } from '@/lib/utils/safeConsole'
import { captureClientException } from '@/lib/analytics/capture-client-exception'

interface MessageErrorBoundaryProps {
  children: ReactNode
  /** Raw message text, shown as a fallback if rendering fails. */
  fallbackContent?: string
  /** Changes to this key reset the boundary (e.g. message id or content). */
  resetKey?: string
}

interface MessageErrorBoundaryState {
  hasError: boolean
}

export class MessageErrorBoundary extends Component<
  MessageErrorBoundaryProps,
  MessageErrorBoundaryState
> {
  state: MessageErrorBoundaryState = { hasError: false }
  /**
   * The resetKey active when we last caught an error, and a settle timer.
   * We only auto-recover after the resetKey has STOPPED changing for a short
   * window (stream finished) — this prevents a throw→reset→throw loop during
   * streaming (resetKey = id:content changes every token), which manifested as
   * the finalized message flickering blank until the user pressed stop.
   */
  private erroredResetKey: string | undefined = undefined
  private settleTimer: ReturnType<typeof setTimeout> | null = null

  static getDerivedStateFromError(): MessageErrorBoundaryState {
    return { hasError: true }
  }

  private clearSettleTimer() {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer)
      this.settleTimer = null
    }
  }

  componentDidUpdate(prevProps: MessageErrorBoundaryProps) {
    if (!this.state.hasError) return
    if (prevProps.resetKey === this.props.resetKey) return

    // The content is still changing (streaming). Debounce: only attempt
    // recovery once the resetKey has been stable for ~400ms (stream settled),
    // and never re-attempt the exact key we already failed on.
    this.clearSettleTimer()
    const keyAtSchedule = this.props.resetKey
    this.settleTimer = setTimeout(() => {
      if (
        this.state.hasError &&
        this.props.resetKey === keyAtSchedule &&
        this.props.resetKey !== this.erroredResetKey
      ) {
        this.setState({ hasError: false })
      }
    }, 400)
  }

  componentWillUnmount() {
    this.clearSettleTimer()
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.erroredResetKey = this.props.resetKey
    safeConsole.warn('Message render error contained by boundary:', error, info?.componentStack)

    // Report to PostHog with React's component stack. The component stack is a
    // readable list of component display names (e.g. "at Message\n at
    // ChatMessages"), so it identifies the offending component WITHOUT relying
    // on JS source maps — critical for diagnosing the #185 render loop whose
    // minified JS frames are opaque. We fold the top of the component stack into
    // the reported error message because that is preserved verbatim in PostHog
    // ($exception_message), whereas extra context labels are sanitized/truncated.
    try {
      const componentStack = info?.componentStack ?? ''
      const topComponents = componentStack
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 8)
        .join(' > ')
      const reportedError = new Error(
        `${error.message} | reactComponentStack: ${topComponents || 'unavailable'}`
      )
      reportedError.name = error.name
      reportedError.stack = error.stack

      captureClientException(reportedError, {
        source: 'message-error-boundary',
        severity: 'error'
      })
    } catch {
      // Never let telemetry throw from an error boundary.
    }
  }

  render() {
    if (this.state.hasError) {
      const text = this.props.fallbackContent?.trim()
      return (
        <div className="prose min-w-full break-words dark:prose-invert" data-message-content>
          {text ? (
            <p className="whitespace-pre-wrap">{text}</p>
          ) : (
            <p className="text-muted-foreground text-sm italic">
              This message could not be displayed.
            </p>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
